import { useAtom } from 'jotai';
import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { workflowAgentAtom } from '@frontend/atoms';
import { useInstanceId } from '@frontend/hooks';

import { useWorkflow } from './useWorkflow';

type NodeContext = {
  instanceId: string;
  workflowAgentId: string;
  nodeData?: Record<string, unknown>;
};

/**
 * Safely get node data as Record for API context
 */
function getNodeDataAsRecord(data: unknown): Record<string, unknown> | undefined {
  if (!data || typeof data !== 'object') return undefined;
  return data as Record<string, unknown>;
}

type UseNodeDataOptions = {
  nodeId: string;
  nodeType: string;
  enabled?: boolean;
};

type UseNodeOperationsOptions = {
  nodeId: string;
  nodeType: string;
};

// Hook for fetching node data
export function useNodeData({ nodeId, nodeType, enabled = true }: UseNodeDataOptions) {
  const [workflowAgent] = useAtom(workflowAgentAtom);
  const { instanceId } = useInstanceId();
  const { getNodeConfig } = useWorkflow();

  // Evaluate enableAsyncGet outside of queryFn so React Query can track it properly
  const nodeTypeDefinition = getNodeConfig(nodeType);
  const enableAsyncGet = nodeTypeDefinition?.enableAsyncGet;
  const nodeForId = (workflowAgent.nodes || []).find(n => n.id === nodeId);
  const nodeData = getNodeDataAsRecord(nodeForId?.data);
  const isGetEnabled =
    typeof enableAsyncGet === 'function'
      ? enableAsyncGet(nodeId, nodeData)
      : (enableAsyncGet ?? true);

  const shouldFetchData = isGetEnabled && !!nodeTypeDefinition?.onAsyncGet;

  return useQuery({
    queryKey: ['nodeData', nodeId, workflowAgent.id, isGetEnabled], // Include isGetEnabled in query key
    queryFn: async () => {
      if (!shouldFetchData || !instanceId || !workflowAgent.id || !nodeTypeDefinition?.onAsyncGet) {
        return {};
      }

      const context: NodeContext = {
        instanceId,
        workflowAgentId: workflowAgent.id,
        nodeData,
      };

      return await nodeTypeDefinition.onAsyncGet(nodeId, context);
    },
    enabled: enabled && !!instanceId && !!workflowAgent.id && shouldFetchData,

    retry: (failureCount, error) => {
      // Don't retry on 404 or other client errors
      if (error instanceof Error && error.message.includes('404')) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

// Hook for saving node data
export function useNodeSave({ nodeId, nodeType }: UseNodeOperationsOptions) {
  const [workflowAgent] = useAtom(workflowAgentAtom);
  const { instanceId } = useInstanceId();
  const queryClient = useQueryClient();
  const { getNodeConfig } = useWorkflow();

  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const nodeTypeDefinition = getNodeConfig(nodeType);

      if (!nodeTypeDefinition?.onAsyncSave || !instanceId || !workflowAgent.id) {
        throw new Error('WorkWorkflow agent needs to be saved first');
      }

      // Get the actual node data from the workflow agent state for context
      const actualNodeData = getNodeDataAsRecord(
        (workflowAgent.nodes || []).find(n => n.id === nodeId)?.data
      );

      const context: NodeContext = {
        instanceId,
        workflowAgentId: workflowAgent.id,
        nodeData: actualNodeData, // Use actual node data for context (contains links for existing triggers)
      };

      const result = await nodeTypeDefinition.onAsyncSave(nodeId, data, context);
      return result;
    },
    onSuccess: () => {
      // Invalidate all nodeData queries for this node to ensure enableAsyncGet changes are picked up
      queryClient.invalidateQueries({
        queryKey: ['nodeData', nodeId],
      });

      // Also invalidate broader workflow data if needed
      queryClient.invalidateQueries({
        queryKey: ['workflowAgent', workflowAgent.id],
      });
    },
    onError: error => {
      console.error('Failed to save node data:', error);
    },
  });
}

// Hook for deleting node data
export function useNodeDelete({ nodeId, nodeType }: UseNodeOperationsOptions) {
  const [workflowAgent, dispatch] = useAtom(workflowAgentAtom);
  const { instanceId } = useInstanceId();
  const queryClient = useQueryClient();
  const { getNodeConfig } = useWorkflow();

  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const nodeTypeDefinition = getNodeConfig(nodeType);

      if (!nodeTypeDefinition?.onAsyncDelete || !instanceId || !workflowAgent.id) {
        throw new Error('Node delete operation not available');
      }

      // Get the actual node data from the workflow agent state instead of using form parameters
      const actualNodeData = getNodeDataAsRecord(
        (workflowAgent.nodes || []).find(n => n.id === nodeId)?.data
      );

      const context: NodeContext = {
        instanceId,
        workflowAgentId: workflowAgent.id,
        nodeData: actualNodeData || data, // Fallback to passed data if node not found
      };

      return await nodeTypeDefinition.onAsyncDelete(nodeId, context);
    },
    onSuccess: () => {
      // Remove the specific node data from cache
      queryClient.removeQueries({
        queryKey: ['nodeData', nodeId, nodeType, instanceId, workflowAgent.id],
      });

      // Remove the node from the workflow graph state
      dispatch({ type: 'DELETE_NODES', nodeIds: [nodeId] });

      // Invalidate broader workflow data
      queryClient.invalidateQueries({
        queryKey: ['workflowAgent', workflowAgent.id],
      });
    },
    onError: error => {
      console.error('Failed to delete node data:', error);
    },
  });
}

// Combined hook for all node operations
export function useNodeOperations({ nodeId, nodeType }: UseNodeOperationsOptions) {
  const emptyDataRef = useRef<Record<string, unknown>>({});
  const nodeDataQuery = useNodeData({ nodeId, nodeType });
  const saveNodeMutation = useNodeSave({ nodeId, nodeType });
  const deleteNodeMutation = useNodeDelete({ nodeId, nodeType });

  return {
    // Data fetching
    data: nodeDataQuery.data ?? emptyDataRef.current,
    isLoadingData: nodeDataQuery.isLoading,
    error: nodeDataQuery.error,
    refetch: nodeDataQuery.refetch,

    // Save operation
    saveNode: saveNodeMutation.mutate,
    saveNodeAsync: saveNodeMutation.mutateAsync,
    isSaving: saveNodeMutation.isPending,
    saveError: saveNodeMutation.error,

    // Delete operation
    deleteNode: deleteNodeMutation.mutate,
    deleteNodeAsync: deleteNodeMutation.mutateAsync,
    isDeleting: deleteNodeMutation.isPending,
    deleteError: deleteNodeMutation.error,

    // Combined loading state
    isLoading:
      nodeDataQuery.isLoading || saveNodeMutation.isPending || deleteNodeMutation.isPending,
  };
}
